import React, { useState, FunctionComponent, useMemo, useEffect, ChangeEvent } from 'react';
import { HDate, months } from '@hebcal/core';
import {
  Box,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import { CalendarTodayOutlined } from '@mui/icons-material';
import i18next from 'i18next';

import Dialog from 'components/commons/Dialog';

import { FormGroup, ErrorBlock, HelpText } from 'styles/input';

import { IHebrewDateInputProps } from './types';
import { CustomInput } from './styles';

import './i18n';

const toISO = (d: Date) => d.toISOString().slice(0, 10);
const fromISO = (iso?: string) => (iso ? new Date(`${iso}T00:00:00`) : undefined);

const HEB_MONTH_LABELS: Record<number, string> = {
  [months.TISHREI]: 'Tishre',
  [months.CHESHVAN]: 'Jeshvan',
  [months.KISLEV]: 'Kislev',
  [months.TEVET]: 'Tebet',
  [months.SHVAT]: 'Shevat',
  [months.ADAR_I]: 'Adar Ⅰ',
  [months.ADAR_II]: 'Adar Ⅱ', // (Adar II in leap years)
  [months.NISAN]: 'Nisan',
  [months.IYYAR]: 'Iar',
  [months.SIVAN]: 'Sivan',
  [months.TAMUZ]: 'Tamuz',
  [months.AV]: 'AB',
  [months.ELUL]: 'Elul',
};

function isLeapHebrewYear(hYear: number): boolean {
  const mod = hYear % 19;
  return mod === 0 || [3, 6, 8, 11, 14, 17].includes(mod);
}

function daysInHebrewMonth(y: number, m: number): number {
  // 1st of next month minus 1 day
  const thisMonthFirst = new HDate(1, m, y).greg();
  const next = (() => {
    if (m === months.ELUL) return { y: y + 1, m: months.TISHREI };
    if (m === months.ADAR_I) return { y, m: months.ADAR_II }; // Adar I -> Adar (Adar II)
    return { y, m: m + 1 };
  })();
  const nextMonthFirst = new HDate(1, next.m, next.y).greg();
  return Math.round((+nextMonthFirst - +thisMonthFirst) / (24 * 3600 * 1000));
}

export function formatHebrewFromISO(iso?: string): string {
  if (!iso) return '';
  const g = fromISO(iso);
  if (!g || Number.isNaN(+g)) return '';
  const h = new HDate(g);
  return `${h.getDate()} ${HEB_MONTH_LABELS[h.getMonth()].toUpperCase()} ${h.getFullYear()}`;
}

const HebrewDateInput: FunctionComponent<IHebrewDateInputProps> = (
  props: IHebrewDateInputProps,
) => {
  const {
    className = '',
    disabled = false,
    helpText = '',
    input,
    label = '',
    meta: { touched, error },
    minDate,
    maxDate,
    size = 'medium',
    variant = 'filled',
    yearRange,
  } = props;
  const { name, value, onChange, onBlur, onFocus } = input;
  const hasError = !!touched && !!error;

  const initialHeb = useMemo(() => {
    const g = fromISO(value);
    const h = !g || Number.isNaN(+g) ? new HDate() : new HDate(g);
    return { hy: h.getFullYear(), hm: h.getMonth(), hd: h.getDate() };
  }, [value]);

  const [open, setOpen] = useState(false);
  const [hy, setHy] = useState<number>(initialHeb.hy);
  const [hm, setHm] = useState<number>(initialHeb.hm);
  const [hd, setHd] = useState<number>(initialHeb.hd);

  const display = useMemo(() => formatHebrewFromISO(value), [value]);
  const gMin = useMemo(() => fromISO(minDate), [minDate]);
  const gMax = useMemo(() => fromISO(maxDate), [maxDate]);

  const leap = isLeapHebrewYear(hy);
  const allowedMonths = useMemo(() => {
    const all = Object.keys(HEB_MONTH_LABELS).map(Number);
    return leap ? all : all.filter((m) => m !== months.ADAR_I);
  }, [leap]);

  const maxDay = daysInHebrewMonth(hy, hm);
  const dayOptions = useMemo(() => Array.from({ length: maxDay }, (_, i) => i + 1), [maxDay]);

  useEffect(() => {
    if (initialHeb) {
      setHy(initialHeb.hy);
      setHm(initialHeb.hm);
      setHd(initialHeb.hd);
    }
  }, [initialHeb]);

  useEffect(() => {
    if (hd > maxDay) setHd(maxDay);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxDay]);

  const years = useMemo(() => {
    const min = yearRange?.min ?? 5600;
    const max = yearRange?.max ?? 5900;
    const arr: number[] = [];
    for (let y = min; y <= max; y++) arr.push(y);
    return arr;
  }, [yearRange]);

  const commit = () => {
    const g = new HDate(hd, hm, hy).greg();
    let iso = toISO(g);
    if (gMin && g < gMin) iso = toISO(gMin);
    if (gMax && g > gMax) iso = toISO(gMax);
    onChange(iso);
    setOpen(false);
  };

  const inputTextProps = {
    ...input,
    disabled,
    error: hasError,
    fullWidth: true,
    id: name,
    inputProps: {
      'aria-label': name,
      'data-testid': `input-${name}`,
    },
    label,
    name,
    onFocus,
    onChange: (event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value),
    onBlur,
    size,
    value: display || value,
    variant,
    InputProps: {
      endAdornment: !disabled && (
        <InputAdornment position="end">
          <IconButton
            aria-label="open calendar"
            data-testid="open-calendar-button"
            onClick={() => setOpen(true)}
          >
            <CalendarTodayOutlined />
          </IconButton>
        </InputAdornment>
      ),
    },
  };

  return (
    <>
      <FormGroup className={className}>
        <CustomInput {...inputTextProps} />
        {!!helpText && <HelpText data-testid="help-text">{helpText}</HelpText>}
        {hasError && <ErrorBlock data-testid="error-block">{error}</ErrorBlock>}
      </FormGroup>
      <Dialog
        onCancelClick={() => setOpen(false)}
        onConfirmClick={commit}
        open={open}
        title={label}
      >
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2, mt: 1 }}>
          <FormControl>
            <InputLabel id={`${name}-hy`}>{i18next.t<string>('HEBREW_DATE_INPUT:YEAR')}</InputLabel>
            <Select
              labelId={`${name}-hy`}
              label="Year"
              value={hy}
              onChange={(e) => setHy(Number(e.target.value))}
            >
              {years.map((y) => (
                <MenuItem key={y} value={y}>
                  {y}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl>
            <InputLabel id={`${name}-hm`}>
              {i18next.t<string>('HEBREW_DATE_INPUT:MONTH')}
            </InputLabel>
            <Select
              labelId={`${name}-hm`}
              label="Month"
              value={hm}
              onChange={(e) => setHm(Number(e.target.value))}
            >
              {allowedMonths.map((m) => (
                <MenuItem key={m} value={m}>
                  {HEB_MONTH_LABELS[m].toUpperCase()}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl>
            <InputLabel id={`${name}-hd`}>{i18next.t<string>('HEBREW_DATE_INPUT:DAY')}</InputLabel>
            <Select
              labelId={`${name}-hd`}
              label="Day"
              value={hd}
              onChange={(e) => setHd(Number(e.target.value))}
            >
              {dayOptions.map((d) => (
                <MenuItem key={d} value={d}>
                  {d}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Dialog>
    </>
  );
};

export default HebrewDateInput;
